import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import back_app from '../../lib/back_app';
import { cat_id2lbl } from '../../lib/cat';
import CreatorHeader from '../../components/creator/CreatorHeader';
import PlatformIcon, { platform_lbl } from '../../components/PlatformIcon';
import platform from '../../lib/platform';

type CreatorTier = {
  enabled: boolean;
  payment: number;
};

type Campaign = {
  id: string;
  name: string;
  description: string;
  category: string;
  brand_name: string;
  brand_logo_url?: string;
  asset_types: {
    video_ad: number;
    banner: number;
  };
  budget_info: {
    total_budget: number;
    spent: number;
    remaining: number;
    creator_tiers?: {
      small?: CreatorTier;
      medium?: CreatorTier;
      large?: CreatorTier;
    };
  };
  active_creators_count: number;
  match_score: number;
};

export default function BrowseCampaigns() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const videoId = searchParams.get('video_id');

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [has_social, set_has_social] = useState<boolean | null>(null);
  const [socials, set_socials] = useState<Array<{platform: string, avg_views?: number, med_views?: number, cpm?: number, value: number}>>([]);
  const [any_country, set_any_country] = useState(false);

  
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [matchFilter, setMatchFilter] = useState('all');

  useEffect(() => {
    fetchCampaigns();
  }, [any_country]);

  const fetchCampaigns = async () => {
    try {
      const status_res = await back_app.creator_status_get();
      if (!status_res?.err) {
        set_has_social(!!status_res?.has_social);
        set_socials(status_res?.socials || []);
      }

      const result = await back_app.creator_camp_list({
        ...(videoId ? {video_id: videoId} : {}),
        ...(any_country ? {any_country: true} : {}),
      });

      if (!result.err) {
        setCampaigns(result || []);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      if ((error as Error).message == 'no session') {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  
  const categories = useMemo(() => {
    const cats = new Set(campaigns.map(c => c.category));
    return Array.from(cats).sort();
  }, [campaigns]);

  
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(campaign => {
      
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          campaign.name.toLowerCase().includes(query) ||
          campaign.brand_name.toLowerCase().includes(query) ||
          campaign.description.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      
      if (categoryFilter !== 'all' && campaign.category !== categoryFilter) {
        return false;
      }

      
      if (matchFilter !== 'all') {
        const score = campaign.match_score;
        if (matchFilter === '4' && score !== 4) return false;
        if (matchFilter === '3' && score !== 3) return false;
        if (matchFilter === '2' && score !== 2) return false;
        if (matchFilter === '1' && score !== 1) return false;
      }

      return true;
    });
  }, [campaigns, searchQuery, categoryFilter, matchFilter]);

  const handleCampaignClick = (campaignId: string) => {
    if (videoId) {
      navigate(`/creator/campaigns/${campaignId}?video_id=${videoId}`);
    } else {
      navigate(`/creator/campaigns/${campaignId}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <CreatorHeader />
        <div className="flex items-center justify-center h-96">
          <div className="inline-block w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <CreatorHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {}
        <div className="mb-6">
          <button
            onClick={() => navigate('/creator/videos')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <span>←</span>
            <span>Back to Videos</span>
          </button>

          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Browse Campaigns
          </h1>
          <p className="text-gray-600">
            Find campaigns that match your content and audience
          </p>
        </div>

        {has_social === false && (
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-xl p-5 mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="text-3xl">🔗</div>
            <div className="flex-1">
              <h3 className="font-bold text-gray-900 mb-1">
                Connect a social platform to see campaigns
              </h3>
              <p className="text-sm text-gray-700">
                You need to integrate at least one social platform before you
                can browse and join campaigns.
              </p>
            </div>
            <button
              onClick={() => navigate('/creator/profile')}
              className="px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all whitespace-nowrap"
            >
              Go to Profile →
            </button>
          </div>
        )}

        {}
        <div className="bg-white rounded-xl border p-4 mb-6 space-y-4">
          {}
          <div>
            <input
              type="text"
              placeholder="Search campaigns or brands..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat_id2lbl(cat)}</option>
                ))}
              </select>
            </div>

            {}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Match Score
              </label>
              <select
                value={matchFilter}
                onChange={(e) => setMatchFilter(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Scores</option>
                <option value="4">⭐⭐⭐⭐ Perfect</option>
                <option value="3">⭐⭐⭐ Great</option>
                <option value="2">⭐⭐ Good</option>
                <option value="1">⭐ Fair</option>
              </select>
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={any_country}
              onChange={(e) => set_any_country(e.target.checked)}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm text-gray-700">
              Search campaigns from any country
            </span>
          </label>

          {}
          <div className="text-sm text-gray-600">
            {filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 ? 's' : ''} found
          </div>
        </div>

        {}
        {filteredCampaigns.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
            {filteredCampaigns.map((campaign) => {
              return (
                <div
                  key={campaign.id}
                  className="bg-white rounded-xl border shadow-sm hover:shadow-lg transition-all overflow-hidden"
                >
                  {}
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 border-b">
                    <div className="flex items-center gap-3">
                      {}
                      <div className="w-12 h-12 bg-white rounded-lg border-2 border-blue-200 flex items-center justify-center flex-shrink-0">
                        {campaign.brand_logo_url ? (
                          <img
                            src={campaign.brand_logo_url}
                            alt={campaign.brand_name}
                            className="w-10 h-10 object-contain"
                          />
                        ) : (
                          <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd"/>
                          </svg>
                        )}
                      </div>

                      {}
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-gray-900 truncate">
                          {campaign.brand_name}
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          {[1, 2, 3, 4].map((star) => (
                            <svg
                              key={star}
                              className={`w-4 h-4 ${
                                star <= campaign.match_score
                                  ? 'text-yellow-400 fill-current'
                                  : 'text-gray-300'
                              }`}
                              viewBox="0 0 20 20"
                            >
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {}
                  <div className="p-4 space-y-3">
                    {}
                    <h3 className="text-lg font-bold text-gray-900 line-clamp-1">
                      {campaign.name}
                    </h3>

                    {}
                    <div>
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                        {cat_id2lbl(campaign.category)}
                      </span>
                    </div>

                    {}
                    <div className="flex gap-2 text-sm">
                      {campaign.asset_types.video_ad > 0 && (
                        <div className="flex items-center gap-1 text-gray-700">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z"/>
                          </svg>
                          {campaign.asset_types.video_ad}
                        </div>
                      )}
                      {campaign.asset_types.banner > 0 && (
                        <div className="flex items-center gap-1 text-gray-700">
                          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/>
                          </svg>
                          {campaign.asset_types.banner}
                        </div>
                      )}
                    </div>

                    {}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <div className="text-gray-500">Total Budget</div>
                        <div className="font-semibold text-gray-900">
                          ${campaign.budget_info.total_budget.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-500">Remaining</div>
                        <div className="font-semibold text-green-600">
                          ${campaign.budget_info.remaining.toLocaleString()}
                        </div>
                      </div>
                    </div>

                    {socials.length > 0 && (
                      <div className="border-t pt-3">
                        <div className="text-xs font-semibold text-gray-700 mb-2">
                          Estimated payout per platform
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {socials.map((s) => {
                            return (
                              <div
                                key={s.platform}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg"
                              >
                                <PlatformIcon platform={s.platform} className="w-5 h-5"/>
                                <div>
                                  <div className="text-xs text-gray-600">{platform_lbl(s.platform)}</div>
                                  <div className="text-sm font-bold text-green-600">
                                    ${platform.payout_est(s).toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {}
                    <div className="text-sm">
                      <div className="flex items-center gap-2 text-gray-700">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z"/>
                        </svg>
                        <span className="font-semibold">{campaign.active_creators_count}</span>
                        <span className="text-gray-500">Active Creators</span>
                      </div>
                    </div>

                    {}
                    <button
                      onClick={() => handleCampaignClick(campaign.id)}
                      className="w-full mt-4 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all"
                    >
                      View Details & Apply
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl border">
            <div className="text-6xl mb-4">🔍</div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              No campaigns found
            </h3>
            <p className="text-gray-600">
              Try adjusting your filters
            </p>
          </div>
        )}
      </div>
    </div>
  );
}